import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaItem } from '@/types/media';

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[index];

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[440px] w-full md:h-[600px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={item.backdrop}
              alt={item.title}
              title={`${item.title} - 天堂影视焦点推荐`}
              className="h-full w-full object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 to-transparent"></div>

        <div className="container-page relative flex h-full items-end pb-10 md:items-center md:pb-0">
          <div className="w-full max-w-2xl animate-fade-up" key={current.id}>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-primary-950">
              <i className="ri-fire-fill"></i>
              焦点推荐 · {current.type === 'movie' ? '电影' : '电视剧'}
            </span>
            <h1 className="mt-4 text-3xl font-bold text-background-50 md:text-5xl">
              {current.title}
            </h1>
            <p className="mt-3 text-sm text-accent-300 md:text-base">{current.tagline}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-background-200">
              <span className="flex items-center gap-1 font-semibold text-accent-400">
                <i className="ri-star-fill"></i>
                {current.rating.toFixed(1)}
              </span>
              <span>{current.year}</span>
              <span>{current.region}</span>
              <span className="hidden sm:inline">{current.duration}</span>
              <span className="flex flex-wrap gap-2">
                {current.genres.map((g) => (
                  <span key={g} className="rounded bg-background-50/15 px-2 py-0.5 text-xs">
                    {g}
                  </span>
                ))}
              </span>
            </div>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200/80 md:line-clamp-3">
              {current.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 rounded-md border border-background-50/30 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line text-lg"></i>
                查看详情
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 backdrop-blur transition-colors hover:bg-accent-500 hover:text-primary-950 md:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 backdrop-blur transition-colors hover:bg-accent-500 hover:text-primary-950 md:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-4 right-4 flex gap-2 md:right-6">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-6 bg-accent-500' : 'w-2 bg-background-50/50 hover:bg-background-50'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}